import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import io from "socket.io-client";
import { BASE_URL } from "../../redux/ApiEndPoints";

export default function InstructorNotification() {
  const dispatch = useDispatch();
  const { students, courses, metrics } = useSelector((state) => state.admin);
  const auth = useSelector((state) => state.auth);
  const adminName = auth?.full_name || "Admin";

  // Debug auth object

  const [allStudent, setAllStudent] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [instructorStats, setInstructorStats] = useState({});

  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const bellRef = useRef();

  // Fetch notifications from API on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await fetch(BASE_URL + "notifications", {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.notifications)) {
          setNotifications(
            data.notifications.map((n) => ({
              ...n,
              timestamp:
                n.created_at || n.timestamp || new Date().toISOString(),
            }))
          );
          setUnreadCount(data.notifications.filter((n) => !n.is_read).length);
        }
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      }
    };
    // Call the async function
    if (auth?.token) {
      fetchNotifications();
    } else {
      console.warn("No auth token available");
    }

    return () => { };
  }, [auth?.token, dispatch]);

  // Socket.IO for instructor notifications
  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL, {
      withCredentials: true,
      transports: ["websocket"],
    });

    socket.on("connect", () => {
      socket.emit("joinInstructorRoom", {
        instructorId: auth?.user_id || auth?.id,
      });
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
    });

    socket.on("instructorNotification", (notification) => {
      setNotifications((prev) => {
        const newNotifications = [
          {
            ...notification,
            timestamp: notification.timestamp || new Date().toISOString(),
            is_read: false,
          },
          ...prev,
        ];
        return newNotifications;
      });
      setUnreadCount((prev) => prev + 1);
    });

    socket.on("disconnect", () => { });

    return () => {
      socket.disconnect();
    };
  }, [auth?.user_id, auth?.user_id]);

  // Mark notifications as read when dropdown is opened
  useEffect(() => {
    if (dropdownOpen && notifications.length > 0) {
      const unreadIds = notifications
        .filter((n) => !n.is_read)
        .map((n) => n.id)
        .filter(Boolean);
      if (unreadIds.length > 0) {
        fetch(BASE_URL + "notifications/mark-read", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.token}`,
          },
          body: JSON.stringify({ ids: unreadIds }),
        }).then(() => {
          setNotifications((prev) =>
            prev.map((n) =>
              unreadIds.includes(n.id) ? { ...n, is_read: true } : n
            )
          );
        });
      }
      setUnreadCount(0);
    }
  }, [dropdownOpen, notifications, auth?.token]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (bellRef.current && !bellRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // Get notification icon based on event type
  const getNotificationIcon = (event) => {
    switch (event) {
      // Course Management
      case "new-enrollment":
        return "👨‍🎓";
      case "course-assigned":
        return "📚";
      case "course-published":
        return "✅";
      case "course-unpublished":
        return "🚫";
      case "course-approved":
        return "🎉";
      case "course-rejected":
        return "❌";
      case "course-updated":
        return "✏️";
      case "course-deleted":
        return "🗑️";
      case "course-created":
        return "🆕";
      case "course-information-updated":
        return "📝";
      case "course-publish-requested":
        return "📤";
      case "course-unpublish-requested":
        return "📤";

      // Course Requests
      case "publish-request-approved":
        return "✅";
      case "publish-request-declined":
        return "❌";
      case "unpublish-request-approved":
        return "✅";
      case "unpublish-request-declined":
        return "❌";

      // Student Management
      case "student-deactivated":
        return "⏸️";
      case "student-activated":
        return "▶️";
      case "student-unenrolled":
        return "👋";
      // Certificate Management
      case "certificate-requested":
        return "📜";
      case "certificate-approved":
        return "✅";
      case "certificate-rejected":
        return "❌";

      // Profile & Account
      case "profile-edited-by-admin":
        return "👤";
      case "account-deactivated":
        return "🔒";
      case "account-activated":
        return "🔓";

      // Analytics & Reports
      case "weekly-report":
        return "📊";
      case "monthly-report":
        return "📈";
      case "course-analytics":
        return "📊";
      case "student-engagement":
        return "📈";

      // Reminders & Notifications
      case "draft-reminder":
        return "⏰";
      case "course-completion-reminder":
        return "⏰";
      case "student-inactivity":
        return "📉";

      // System Notifications
      case "system-maintenance":
        return "🔧";
      case "feature-update":
        return "🆕";
      case "security-alert":
        return "🚨";

      // Content Moderation
      case "content-flagged":
        return "🚩";
      case "inappropriate-content":
        return "⚠️";

      // Payment & Revenue
      case "payment-received":
        return "💰";
      case "revenue-report":
        return "💵";
      case "subscription-expired":
        return "⏰";

      // Learning
      case "learning-milestone":
        return "🏆";
      case "course-recommendation":
        return "💡";

      default:
        return "🔔";
    }
  };

  // Get notification category for styling
  const getNotificationCategory = (event) => {
    // Success notifications (positive actions)
    if (
      [
        "new-enrollment",
        "course-published",
        "course-approved",
        "publish-request-approved",
        "unpublish-request-approved",
        "student-activated",
        "account-activated",
        "payment-received",
        "learning-milestone",
        "course-created",
        "course-information-updated",
        "publish-approved",
        "certificate-approved",
      ].includes(event)
    )
      return "success";

    // Danger notifications (negative actions)
    if (
      [
        "course-unpublished",
        "course-rejected",
        "publish-request-declined",
        "unpublish-request-declined",
        "student-deactivated",
        "account-deactivated",
        "course-deleted",
        "student-unenrolled",
        "content-flagged",
        "inappropriate-content",
        "security-alert",
        "certificate-rejected",
      ].includes(event)
    )
      return "danger";

    // Warning notifications (attention needed)
    if (
      [
        "draft-reminder",
        "course-completion-reminder",
        "student-inactivity",
        "subscription-expired",
        "system-maintenance"
      ].includes(event)
    )
      return "warning";

    // Info notifications (general updates)
    if (
      [
        "course-assigned",
        "course-updated",
        "profile-edited-by-admin",
        "weekly-report",
        "monthly-report",
        "course-analytics",
        "student-engagement",
        "revenue-report",
        "feature-update",
        "course-recommendation",
        "course-publish-requested",
        "course-unpublish-requested",
        "certificate-requested",
      ].includes(event)
    )
      return "info";

    return "default";
  };

  const formatNotification = (notif) => {
    let data;
    try {
      data =
        typeof notif.data === "string" ? JSON.parse(notif.data) : notif.data;
    } catch (error) {
      console.error("Failed to parse instructor notification data:", error);
      return "Invalid notification data.";
    }

    switch (notif.event) {
      // Course Management
      case "new-enrollment":
        return `New student enrolled: ${data.studentName || data.studentId} enrolled in "${data.courseName || 'Course'}"`;
      case "course-assigned":
        return `Course assigned: "${data.courseName || 'Course'}" has been assigned to you`;
      case "course-published":
        return `Course published: "${data.courseName || 'Course'}" is now live and available to students`;
      case "course-unpublished":
        return `Course unpublished: "${data.courseName || 'Course'}" has been unpublished`;
      case "course-approved":
        return `Course approved: "${data.courseName || 'Course'}" has been approved by admin`;
      case "course-rejected":
        return `Course rejected: "${data.courseName || 'Course'}" has been rejected by admin`;
      case "course-updated":
        return `Course updated: "${data.courseName || 'Course'}" has been updated with new content`;
      case "course-deleted":
        return `Course deleted: "${data.courseName || 'Course'}" has been deleted`;
      case "course-created":
        return `Course created: "${data.courseName || 'Course'}" has been created and is in draft status`;
      case "course-information-updated":
        return `Course information updated: "${data.courseName || 'Course'}" information has been updated`;
      case "course-publish-requested":
        return `Publish request submitted: "${data.courseName || 'Course'}" publish request has been submitted for admin approval`;
      case "course-unpublish-requested":
        return `Unpublish request submitted: "${data.courseName || 'Course'}" unpublish request has been submitted for admin approval`;
      case "publish-approved":
        return `Publish request approved: "${data.courseName || 'Course'}" publish request has been approved`;
      case "assignment-submitted":
        return `Assignment '${data.assignmentTitle || data.assignmentId
          }' submitted by ${data.studentName || data.studentId} in '${data.courseName || data.courseId
          }'.`;
      case "review-replied":
        return `Reply to your course review on '${data.courseName || data.courseId
          }' by ${data.userName || data.userId}: "${data.content}"`;
      case "comment-replied":
        return `Reply to your video comment${data.videoTitle ? ` on '${data.videoTitle}'` : ""
          } by ${data.userName || data.userId}: "${data.content}"`;
      case "comment-upvoted":
        return `Your comment upvoted${data.videoTitle ? ` on '${data.videoTitle}'` : ""
          } by ${data.userName || data.userId}: "${data.content}"`;
      case "comment-downvoted":
        return `Your comment downvoted${data.videoTitle ? ` on '${data.videoTitle}'` : ""
          } by ${data.userName || data.userId}: "${data.content}"`;
      case "comment-flagged":
        return `Your comment flagged${data.videoTitle ? ` on '${data.videoTitle}'` : ""
          } by ${data.userName || data.userId}: "${data.content}"`;
      // Course Requests
      case "publish-request-approved":
        return `Publish request approved: "${data.courseName || 'Course'}" publish request has been approved`;
      case "publish-request-declined":
        return `Publish request declined: "${data.courseName || 'Course'}" publish request has been declined`;
      case "unpublish-request-approved":
        return `Unpublish request approved: "${data.courseName || 'Course'}" unpublish request has been approved`;
      case "unpublish-request-declined":
        return `Unpublish request declined: "${data.courseName || 'Course'}" unpublish request has been declined`;
      case "draft-reminder":
        return `Draft reminder: "${data.courseName || 'Course'}" has been in draft for ${data.daysInDraft || 0} days`;
      case "profile-edited":
        return `Your profile has been edited by admin`;
      case "weekly-report":
        return `Weekly report: You have ${data.totalEnrolledStudents || 0
          } total enrolled students across all your courses`;
      // Student Management
      case "student-deactivated":
        return `Student deactivated: ${data.studentName || data.studentId} (${data.studentEmail || ''}) who was enrolled in your course`;
      case "student-activated":
        return `Student activated: ${data.studentName || data.studentId} (${data.studentEmail || ''}) who was enrolled in your course`;
      case "student-unenrolled":
        return `Student unenrolled: ${data.studentName || data.studentId} has been unenrolled from "${data.courseName || 'Course'}"`;

      // Certificate Management
      case "certificate-requested":
        return `Certificate requested: "${data.courseName || 'Course ID ' + data.courseId}" by Student ${data.studentName || data.studentId}`;
      case "certificate-approved":
        return `Certificate approved: "${data.courseName || 'Course ID ' + data.courseId}" for Student ${data.studentName || data.studentId}`;
      case "certificate-rejected":
        return `Certificate rejected: "${data.courseName || 'Course ID ' + data.courseId}" for Student ${data.studentName || data.studentId} - Reason: ${data.reason || 'No reason provided'}`;

      // Profile & Account
      case "profile-edited-by-admin":
        return `Profile updated: Your profile has been updated by administrator`;
      case "account-deactivated":
        return `Account deactivated: Your instructor account has been deactivated`;
      case "account-activated":
        return `Account activated: Your instructor account has been activated`;

      // Analytics & Reports
      case "monthly-report":
        return `Monthly report: ${data.totalCourses || 0} courses, ${data.totalStudents || 0} students, ${data.totalRevenue || 0} revenue`;
      case "course-analytics":
        return `Course analytics: "${data.courseName || 'Course'}" has ${data.engagementRate || 0}% engagement rate`;
      case "student-engagement":
        return `Student engagement: ${data.engagementLevel || 'High'} engagement in "${data.courseName || 'Course'}"`;

      // Reminders & Notifications
      case "course-completion-reminder":
        return `Completion reminder: "${data.courseName || 'Course'}" completion rate is ${data.completionRate || 0}%`;
      case "student-inactivity":
        return `Student inactivity: ${data.inactiveStudents || 0} students haven't accessed "${data.courseName || 'Course'}" recently`;

      // System Notifications
      case "system-maintenance":
        return `System maintenance: Scheduled for ${data.date || 'soon'} - Duration: ${data.duration || 'Unknown'}`;
      case "feature-update":
        return `Feature update: ${data.featureName || 'New feature'} is now available`;
      case "security-alert":
        return `Security alert: ${data.alertType || 'Security issue'} - Please review immediately`;

      // Content Moderation
      case "content-flagged":
        return `Content flagged: "${data.contentType || 'Content'}" in "${data.courseName || 'Course'}" has been flagged`;
      case "inappropriate-content":
        return `Inappropriate content: Content in "${data.courseName || 'Course'}" requires review`;

      // Payment & Revenue
      case "payment-received":
        return `Payment received: $${data.amount || 0} from course "${data.courseName || 'Course'}"`;
      case "revenue-report":
        return `Revenue report: $${data.totalRevenue || 0} total revenue this ${data.period || 'month'}`;
      case "subscription-expired":
        return `Subscription expired: Your premium subscription has expired`;

      // Learning
      case "learning-milestone":
        return `Learning milestone: Students in "${data.courseName || 'Course'}" have reached a learning milestone`;
      case "course-recommendation":
        return `Course recommendation: Consider adding "${data.recommendedTopic || 'new content'}" to "${data.courseName || 'Course'}"`;
      default:
        return notif.event;
    }
  };

  // Clear all notifications
  const handleClearAll = async () => {
    await fetch(BASE_URL + "notifications/clear", {
      method: "POST",
      headers: { Authorization: `Bearer ${auth.token}` },
    });
    setNotifications([]);
    setUnreadCount(0);
  };

  return (
    <>
      <div ref={bellRef} style={{ position: "relative" }}>
        <div
          className="notification-bell-container"
          onClick={() => {
            setDropdownOpen((open) => !open);
          }}
          style={{
            position: "relative",
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            background: "#F05203",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
            transition: "all 0.3s ease",
            transform: dropdownOpen ? "scale(1.05)" : "scale(1)",
          }}
        >
          <span
            className="icon-bell"
            style={{
              fontSize: "20px",
              color: "#fff",
            }}
          />
          {unreadCount > 0 && (
            <div
              style={{
                position: "absolute",
                top: "-4px",
                right: "-4px",
                background: "#F05203",
                color: "#fff",
                borderRadius: "50%",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: "bold",
                border: "2px solid #fff",
                animation: "pulse 2s infinite",
              }}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </div>
          )}
        </div>
        {dropdownOpen && (
          <div
            className="notification-dropdown"
            style={{
              position: "absolute",
              right: "0",
              top: "56px",
              maxWidth: "400px",
              width: "calc(100vw - 64px)",
              maxHeight: "500px",
              background: "#fff",
              borderRadius: "16px",
              boxShadow:
                "0 20px 40px rgba(0, 0, 0, 0.1), 0 6px 16px rgba(0, 0, 0, 0.08)",
              zIndex: 1000,
              overflow: "hidden",
              border: "1px solid rgba(0, 0, 0, 0.08)",
              animation: "slideIn 0.3s ease-out",
            }}
          >
            <div
              style={{
                padding: "20px 24px",
                background: "#F05203",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <h4
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#fff",
                  }}
                >
                  Notifications
                </h4>
                <p style={{ margin: 0, fontSize: "14px", opacity: 0.9 }}>
                  {notifications.length}{" "}
                  {notifications.length <= 1
                    ? "notification"
                    : "notifications"}
                </p>
              </div>
              {notifications.length > 0 && (
                <button
                  onClick={handleClearAll}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    border: "none",
                    borderRadius: "8px",
                    color: "#fff",
                    padding: "6px 12px",
                    fontSize: "12px",
                    cursor: "pointer",
                    transition: "background 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.background = "rgba(255, 255, 255, 0.3)")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.background = "rgba(255, 255, 255, 0.2)")
                  }
                >
                  Clear All
                </button>
              )}
            </div>
            <div
              style={{
                maxHeight: "400px",
                overflowY: "auto",
                padding: "0",
              }}
            >
              {notifications.length === 0 ? (
                <div
                  style={{
                    padding: "40px 24px",
                    textAlign: "center",
                    color: "#9ca3af",
                  }}
                >
                  <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                    👨‍🏫
                  </div>
                  <p style={{ margin: 0, fontSize: "16px", fontWeight: "500" }}>
                    No notifications yet
                  </p>
                  <p style={{ margin: "8px 0 0 0", fontSize: "14px" }}>
                    We'll notify you about course activities and student
                    interactions
                  </p>
                </div>
              ) : (
                notifications.map((notif, idx) => {
                  const category = getNotificationCategory(notif.event);
                  const isUnread = idx < unreadCount;

                  return (
                    <div
                      key={idx}
                      style={{
                        padding: "16px 24px",
                        borderBottom:
                          idx === notifications.length - 1
                            ? "none"
                            : "1px solid #f3f4f6",
                        background: isUnread ? "#f8faff" : "#fff",
                        position: "relative",
                        transition: "background 0.2s ease",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        if (!isUnread) e.target.style.background = "#f9fafb";
                      }}
                      onMouseLeave={(e) => {
                        if (!isUnread) e.target.style.background = "#fff";
                      }}
                    >
                      {isUnread && (
                        <div
                          style={{
                            position: "absolute",
                            left: "0",
                            top: "0",
                            bottom: "0",
                            width: "4px",
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          }}
                        />
                      )}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "50%",
                            background:
                              category === "success"
                                ? "#ecfdf5"
                                : category === "danger"
                                  ? "#fef2f2"
                                  : category === "warning"
                                    ? "#fffbeb"
                                    : category === "info"
                                      ? "#eff6ff"
                                      : "#f3f4f6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            flexShrink: 0,
                          }}
                        >
                          {getNotificationIcon(notif.event)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: "14px",
                              lineHeight: "1.4",
                              color: "#374151",
                              marginBottom: "4px",
                              fontWeight: isUnread ? "500" : "400",
                            }}
                          >
                            {formatNotification(notif)}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#9ca3af",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <span>⏰</span>
                            {new Date(notif.timestamp).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        @keyframes slideIn {
          0% {
            opacity: 0;
            transform: translateY(-10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .notification-bell-container:hover {
          transform: scale(1.05) !important;
          box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4) !important;
        }

        .notification-dropdown::-webkit-scrollbar {
          width: 6px;
        }

        .notification-dropdown::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .notification-dropdown::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .notification-dropdown::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </>
  );
}
